<script setup>
import {computed, ref} from "vue";
import {useWindowSize} from "@vueuse/core";
import {useNeoStore} from "../../../../state/neo.js";
import FeatureBlocker from "../../components/ui-components/FeatureBlocker.vue";

const {width} = useWindowSize();
const isMobile = computed(() => {
    return width.value < 900
});


const neoStore = useNeoStore();

const props = defineProps({
    showAside: Boolean,
    thin: Boolean,
    settings: Boolean,
    filled: Boolean,
    narrowAside: Boolean,
    semiNarrowAside: Boolean,
    modalAside: Boolean,
    feature: String,
    version: String
})

const asideMainWidth = computed(() => {
    if (neoStore.asideExpanded) {
        return {
            aside: 'max',
            main: 'min'
        }
    }

    if (props.settings) {
        return {
            aside: 'w-full',
            main: ''
        }
    }
    if (props.thin) {
        return {
            aside: 'thin',
            main: 'thin'
        }
    }
    if (props.narrowAside) {
        return {
            aside: 'narrow',
            main: 'narrow'
        }
    }
    if (props.semiNarrowAside) {
        return {
            aside: 'seminarrow',
            main: 'seminarrow'
        }
    }
    return {
        aside: 'open',
        main: 'collapsed'
    }
})
</script>

<template>
    <template v-if="isMobile">
        <div class="h-full w-screen overflow-x-hidden flex">
            <header class="fixed w-full top-0 z-40 right-0" id="application-topbar">
                <slot name="topbar"></slot>
            </header>
            <main :class="['main-resize grow', filled ? 'bg-white dark:bg-black' : '']" id="application-content">
                <feature-blocker v-if="feature" class="!absolute w-full bottom-[56px] top-0" unrounded :version="version" :name="feature">
                    <slot name="content"></slot>
                </feature-blocker>
                <slot v-else name="content"></slot>
            </main>
            <transition name="fade-overlay">
                <div v-if="showAside" class="fixed h-screen w-screen z-40 bg-black/30"></div>
            </transition>
            <transition name="slide-up-aside">
                <aside v-if="showAside"
                       class="fixed z-50 bg-white bottom-0 top-32 overflow-y-auto rounded-t-3xl w-full overflow-hidden">
                    <feature-blocker v-if="feature" unrounded :version="version" :name="feature">
                        <slot name="aside"></slot>
                    </feature-blocker>
                    <slot v-else name="aside"></slot>
                </aside>
            </transition>
            <slot name="bottombar"></slot>
        </div>
    </template>
    <template v-else>
        <div class="h-full w-screen overflow-x-hidden flex">
            <menu class="bg-white dark:bg-transparent dark:border-neutral-700 border-r h-full" id="application-sidebar">
                <slot name="sidebar"></slot>
            </menu>
            <header class="fixed w-full top-0 z-30 pointer-events-none right-0" id="application-topbar">
                <slot name="topbar"></slot>
            </header>
            <main :class="['main-resize relative group', showAside && !modalAside ? asideMainWidth.main : '', filled ? 'bg-white dark:bg-black' : '']" id="application-content">
                <div v-if="showAside && !modalAside" class="bg-black/15 w-full h-full absolute z-50 opacity-100 group-hover:opacity-0 pointer-events-none transition-all" style="transition-duration: .8s"></div>
                <feature-blocker v-if="feature" unrounded :version="version" :name="feature">
                    <slot name="content"></slot>
                </feature-blocker>
                <slot v-else name="content"></slot>
            </main>

            <template v-if="modalAside">
                <transition name="fade-overlay">
                    <div v-if="showAside" class="fixed inset-0 z-40 bg-black/40"></div>
                </transition>
                <transition name="modal-aside">
                    <div v-if="showAside" class="fixed inset-0 z-50 flex items-center justify-center p-6">
                        <div id="application-aside-modal" class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] overflow-hidden flex flex-col">
                            <feature-blocker v-if="feature" unrounded :version="version" :name="feature">
                                <slot name="aside"></slot>
                            </feature-blocker>
                            <slot v-else name="aside"></slot>
                        </div>
                    </div>
                </transition>
            </template>
            <transition v-else name="slide-aside">
                <aside v-if="showAside" :class="asideMainWidth.aside" id="application-aside">
                    <feature-blocker v-if="feature" unrounded :version="version" :name="feature">
                        <slot name="aside"></slot>
                    </feature-blocker>
                    <slot v-else name="aside"></slot>
                </aside>
            </transition>
        </div>
    </template>
</template>

<style scoped lang="scss">
$sidebar: 104px;
$main: calc(100vw - 104px);
$main-collapsed: calc(calc(100vw - 104px) / 2);
$aside: calc(calc(100vw - 104px) / 2);
$main-thin: calc(calc(100vw - 104px) * 1 / 3);
$aside-wide: calc(calc(100vw - 104px) * 2 / 3);
$aside-narrow: 460px;
$main-narrow: calc(100vw - 104px - 460px);
$aside-seminarrow: 660px;
$main-seminarrow: calc(100vw - 104px - 660px);

#application-sidebar {
    width: $sidebar !important;
}

#application-content {
    width: $main;

    &.collapsed {
        width: $main-collapsed !important;
    }

    &.thin {
        width: $main-thin !important;
    }

    &.narrow {
        width: $main-narrow !important;
    }

    &.seminarrow {
        width: $main-seminarrow !important;
    }

    &.min {
        width: 0 !important;
        pointer-events: none;
        opacity: 0;
    }
}

#application-aside {

    width: 0;

    &.open {
        transition: width .3s ease-in-out;
        width: $aside !important;
    }

    &.thin {
        transition: width .3s ease-in-out;
        width: $aside-wide !important;
    }

    &.narrow {
        transition: width .3s ease-in-out;
        width: $aside-narrow !important;
        flex-shrink: 0;
    }

    &.seminarrow {
        transition: width .3s ease-in-out;
        width: $aside-seminarrow !important;
        flex-shrink: 0;
    }

    &.max {
        width: $main !important;
    }
}

.slide-aside-enter-active,
.slide-aside-leave-active {
    transition: transform 0.3s ease-in-out;
}

.slide-aside-enter-from,
.slide-aside-leave-to {
    max-width: 0;
    transform: translateX(100%);
    opacity: 0;
}

.main-resize {
    transition: width 0.3s ease;
}

.slide-up-aside-enter-active,
.slide-up-aside-leave-active {
    transition: transform 0.3s ease;
}

.slide-up-aside-enter-from,
.slide-up-aside-leave-to {
    transform: translateY(100%);
}

.fade-overlay-enter-active,
.fade-overlay-leave-active {
    transition: opacity 0.3s ease;
}

.fade-overlay-enter-from,
.fade-overlay-leave-to {
    opacity: 0;
}

.modal-aside-enter-active,
.modal-aside-leave-active {
    transition: opacity .2s ease, transform .2s ease;
}

.modal-aside-enter-from,
.modal-aside-leave-to {
    opacity: 0;
    transform: scale(.97);
}
</style>
